import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BookOpen, Shield, Users, Download, Edit3, Save, X, Loader2, Clock,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2, Heading3, Quote, Undo, Redo
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const MenuBar = ({ editor }) => {
  if (!editor) return null;
  const btn = (active, onClick, Icon, label) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${active ? 'bg-slate-200 text-[#0E346E]' : 'text-slate-500'}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-slate-50 rounded-t-lg" data-testid="editor-toolbar">
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, 'Titre 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, 'Titre 2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, 'Titre 3')}
      <div className="w-px h-5 bg-slate-300 mx-1" />
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Gras')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Italique')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), UnderlineIcon, 'Souligné')}
      <div className="w-px h-5 bg-slate-300 mx-1" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Liste')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Liste numérotée')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), Quote, 'Citation')}
      <div className="w-px h-5 bg-slate-300 mx-1" />
      {btn(false, () => editor.chain().focus().undo().run(), Undo, 'Annuler')}
      {btn(false, () => editor.chain().focus().redo().run(), Redo, 'Refaire')}
    </div>
  );
};

const GuideViewer = ({ content }) => (
  <div
    className="prose prose-slate max-w-none p-6 bg-white rounded-lg border
      [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-[#0E346E] [&_h1]:border-b [&_h1]:border-slate-200 [&_h1]:pb-3 [&_h1]:mb-4
      [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-slate-800 [&_h2]:mt-6 [&_h2]:mb-3
      [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-slate-700 [&_h3]:mt-4 [&_h3]:mb-2
      [&_p]:text-sm [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:mb-2
      [&_li]:text-sm [&_li]:text-slate-600 [&_li]:mb-1
      [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
      [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
      [&_blockquote]:border-l-4 [&_blockquote]:border-[#0E346E] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-500"
    dangerouslySetInnerHTML={{ __html: content || '<p>Aucun contenu pour le moment.</p>' }}
    data-testid="guide-content-viewer"
  />
);

const GuideEditor = ({ content, onSave, onCancel, saving }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapUnderline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: content || '',
  });

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden bg-white">
        <MenuBar editor={editor} />
        <EditorContent
          editor={editor}
          className="min-h-[400px] p-4 [&_.tiptap]:outline-none [&_.tiptap]:min-h-[380px]
            [&_.tiptap_h1]:text-2xl [&_.tiptap_h1]:font-bold [&_.tiptap_h1]:text-[#0E346E] [&_.tiptap_h1]:mb-3
            [&_.tiptap_h2]:text-xl [&_.tiptap_h2]:font-semibold [&_.tiptap_h2]:text-slate-800 [&_.tiptap_h2]:mb-2
            [&_.tiptap_h3]:text-lg [&_.tiptap_h3]:font-medium [&_.tiptap_h3]:text-slate-700 [&_.tiptap_h3]:mb-2
            [&_.tiptap_p]:text-sm [&_.tiptap_p]:text-slate-600 [&_.tiptap_p]:mb-2
            [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-6 [&_.tiptap_ul]:mb-2
            [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-6 [&_.tiptap_ol]:mb-2
            [&_.tiptap_blockquote]:border-l-4 [&_.tiptap_blockquote]:border-slate-300 [&_.tiptap_blockquote]:pl-4 [&_.tiptap_blockquote]:italic"
          data-testid="guide-editor"
        />
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="cancel-edit-btn">
          <X className="w-4 h-4 mr-2" />
          Annuler
        </Button>
        <Button
          onClick={() => onSave(editor?.getHTML() || '')}
          disabled={saving}
          className="bg-[#0E346E] hover:bg-[#0A2850]"
          data-testid="save-guide-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

const Documentation = () => {
  const { isSuperAdmin, isAdmin, getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('user');
  const [userGuide, setUserGuide] = useState(null);
  const [adminGuide, setAdminGuide] = useState(null);
  const [editingUser, setEditingUser] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchGuides = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const [userRes, adminRes] = await Promise.all([
        axios.get(`${API}/guides/user`, { headers }),
        isSuperAdmin() || isAdmin()
          ? axios.get(`${API}/guides/admin`, { headers })
          : Promise.resolve({ data: null })
      ]);
      setUserGuide(userRes.data);
      if (adminRes.data) setAdminGuide(adminRes.data);
    } catch (err) {
      console.error('Error fetching guides:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isSuperAdmin, isAdmin]);

  useEffect(() => { fetchGuides(); }, []);

  const handleSave = async (type, content) => {
    setSaving(true);
    try {
      const title = type === 'user' ? 'Guide Utilisateur GembaCoach' : 'Guide Super Administrateur GembaCoach';
      await axios.put(`${API}/guides/${type}`, { title, content }, { headers: getAuthHeaders() });
      toast.success('Guide enregistré avec succès');
      if (type === 'user') setEditingUser(false);
      else setEditingAdmin(false);
      fetchGuides();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = (filename, displayName) => {
    const link = document.createElement('a');
    link.href = `/${filename}`;
    link.download = displayName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] uppercase">Documentation</h1>
          <p className="text-slate-500 mt-1">Guides d'utilisation de GembaCoach</p>
        </div>
        <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
          <BookOpen className="w-4 h-4 mr-1" />
          Version 2.0
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full max-w-md ${isSuperAdmin() || isAdmin() ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <TabsTrigger value="user" className="flex items-center gap-2" data-testid="user-guide-tab">
            <Users className="w-4 h-4" />
            Guide Utilisateur
          </TabsTrigger>
          {(isSuperAdmin() || isAdmin()) && (
            <TabsTrigger value="admin" className="flex items-center gap-2" data-testid="admin-guide-tab">
              <Shield className="w-4 h-4" />
              Guide Super Admin
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="user" className="mt-4">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Guide Utilisateur
                </CardTitle>
                {userGuide?.last_updated && (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Modifié le {new Date(userGuide.last_updated).toLocaleDateString('fr-FR')} par {userGuide.updated_by}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="outline" size="sm"
                  onClick={() => handleDownload('GUIDE_UTILISATEUR_GEMBACOACH.docx', 'Guide_Utilisateur_GembaCoach.docx')}
                  data-testid="download-user-guide"
                >
                  <Download className="w-4 h-4 mr-1" /> Word
                </Button>
                {isSuperAdmin() && !editingUser && (
                  <Button size="sm" className="bg-[#0E346E] hover:bg-[#0A2850]"
                    onClick={() => setEditingUser(true)} data-testid="edit-user-guide-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingUser ? (
                <GuideEditor content={userGuide?.content} onSave={(c) => handleSave('user', c)} onCancel={() => setEditingUser(false)} saving={saving} />
              ) : (
                <GuideViewer content={userGuide?.content} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="admin" className="mt-4">
          <Card className="border-red-100">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  Guide Super Administrateur
                </CardTitle>
                {adminGuide?.last_updated && (
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Modifié le {new Date(adminGuide.last_updated).toLocaleDateString('fr-FR')} par {adminGuide.updated_by}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {isSuperAdmin() && (
                  <Button variant="outline" size="sm"
                    onClick={() => handleDownload('GUIDE_SUPER_ADMIN_GEMBACOACH.docx', 'Guide_Super_Admin_GembaCoach.docx')}
                    data-testid="download-admin-guide"
                  >
                    <Download className="w-4 h-4 mr-1" /> Word
                  </Button>
                )}
                {isSuperAdmin() && !editingAdmin && (
                  <Button size="sm" className="bg-red-600 hover:bg-red-700"
                    onClick={() => setEditingAdmin(true)} data-testid="edit-admin-guide-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingAdmin ? (
                <GuideEditor content={adminGuide?.content} onSave={(c) => handleSave('admin', c)} onCancel={() => setEditingAdmin(false)} saving={saving} />
              ) : (
                <GuideViewer content={adminGuide?.content} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Documentation;
